/**
 * The cross-site sign-in hop (SM-04). B2C returns only to the environment's sign-in site, so a site
 * on any other address starts a hop with the API, which keeps what to ask B2C for, and sends the
 * person to the sign-in site with the hop's id alone. The sign-in site reads what to ask for from the
 * API, passes them straight to B2C and, once back, gets a single-use code for that site only and
 * sends them back with it in the fragment. The site exchanges the code, with the verifier its browser
 * kept, for B2C's access token, and signs in with it as any site does. The sign-in site shows no
 * page of its own and starts no session.
 */

import { matchPath } from 'react-router'
import { z } from 'zod'

// Before the schemas below: this module can load ahead of main.tsx's own import of it.
import '@/shared/config/zod-config'

import { createApiClient, SignInAgainError } from '@/shared/api/client'
import type { AuthConfig } from '@/shared/config/runtime-config'

import { clearFreshSignIn } from './fresh-sign-in'

const hopsPath = '/site-management/v1/sessions/hops'

/** The sign-in site's page a hop passes through on the way to B2C. */
export const hopPagePath = '/auth/hop'

/** Whether an address's path is the hop page, as the router matches it. */
export function isHopPagePath(pathname: string): boolean {
  return isRoutePath(hopPagePath, pathname)
}

/** Whether an address's path is `path` as the router matches it: percent-decoded, any case, a trailing slash or not. */
export function isRoutePath(path: string, pathname: string): boolean {
  return matchPath({ path }, decodePath(pathname)) !== null
}

// As the router decodes a path before matching it: each segment, an encoded slash kept encoded.
function decodePath(pathname: string): string {
  try {
    return pathname
      .split('/')
      .map((segment) => decodeURIComponent(segment).replace(/\//g, '%2F'))
      .join('/')
  } catch {
    return pathname
  }
}

/** The sign-in site's page a sign-out passes through on the way to B2C's sign-out. */
export const hopSignOutPath = '/auth/sign-out'

/**
 * This browser's half of a hop on the site it started on, and for a renewal who must come back, in
 * one record so neither is ever kept without the other: session storage, this tab only.
 */
const startKey = 'tms.auth.hopStart'

/**
 * B2C's access token a hop brought back. Local storage, as MSAL keeps its tokens, so every tab
 * shares the sign-in; encrypted under the key in the session key cookie, as MSAL encrypts its own.
 */
const tokenKey = 'tms.auth.hopToken'

/** Held while a tab checks for a sign-out and writes or forgets the token, so no tab's sign-out lands in between. */
const tokenLockName = 'tms.auth.hopToken'

/**
 * The cookie holding the key the token is encrypted under. No expiry: it ends with the browser
 * session. `__Host-` on https, so no other address under the same parent domain can set it.
 */
function sessionKeyCookie(): string {
  return window.location.protocol === 'https:' ? '__Host-tms.hopKey' : 'tms.hopKey'
}

/** The last sign-out in any tab of this site, which every tab shares: a mark, no secret. */
const signedOutKey = 'tms.auth.hopSignedOut'

/** On the sign-in site: the hop B2C's answer finishes. */
const pendingKey = 'tms.auth.pendingHop'

/** On the sign-in site: the site a sign-out goes back to once B2C has signed the person out. */
const signOutReturnKey = 'tms.auth.signOutReturn'

/** Renew this long before the token expires, so no call races its expiry. */
const expiryMarginMs = 120_000

/** On a site: when this tab last came back from a hop with a token. */
const hopReturnedKey = 'tms.auth.hopReturnedAt'

/**
 * For this long after a hop came back, no new one starts: a token B2C issued with little time left,
 * or a clock running ahead of B2C's, would otherwise send the browser round again and again.
 */
const renewalPauseMs = 5 * 60_000

/** Whether this tab came back from a hop too recently to start another. */
export function isHopJustDone(now: number = Date.now()): boolean {
  const returnedAt = hopReturnedAt()
  return returnedAt !== null && now - returnedAt < renewalPauseMs
}

/** When this tab last came back from a hop with a token, or `null`. */
export function hopReturnedAt(): number | null {
  const returnedAt = Number(readRaw(sessionStorage, hopReturnedKey))
  return Number.isFinite(returnedAt) && returnedAt > 0 ? returnedAt : null
}

/** Leaves for another address. An object, so tests can watch it. */
export const hopNavigation = {
  assign(url: string): void {
    window.location.assign(url)
  },
  replace(url: string): void {
    window.location.replace(url)
  },
  /** Loads `url` afresh even when only its fragment differs from this page's, which `replace` alone would not. */
  reload(url: string): void {
    window.location.replace(url)
    window.location.reload()
  },
}

/**
 * Thrown for a token that has expired where it can only be renewed through the sign-in site: for a
 * check the app makes on its own, which never leaves the page, and for a call that started the
 * renewal, so no request goes without a token. A save shows it as any sign-in that must be renewed.
 */
export class SignInRenewalRequiredError extends SignInAgainError {
  constructor(message = 'The sign-in has expired and is renewed through the sign-in site.') {
    super(message)
    this.name = 'SignInRenewalRequiredError'
  }
}

/**
 * Thrown for a token a hop brought back already expired on this device's clock: the clock is wrong,
 * and no renewal helps until it is set right.
 */
export class DeviceClockError extends SignInRenewalRequiredError {
  constructor() {
    super('The sign-in came back already expired on this device’s clock. Set the clock to the right time, then reload the page.')
    this.name = 'DeviceClockError'
  }
}

let isDeviceClockWrong = false
const deviceClockListeners = new Set<() => void>()

/** Notes whether the last token a hop brought back could be used on this device's clock. */
export function reportDeviceClock(isRight: boolean): void {
  if (isDeviceClockWrong === !isRight) {
    return
  }
  isDeviceClockWrong = !isRight
  for (const listener of deviceClockListeners) {
    listener()
  }
}

/** Whether this device's clock was last found wrong, so the page can say so. */
export function readDeviceClockWrong(): boolean {
  return isDeviceClockWrong
}

/** Calls `listener` whenever {@link readDeviceClockWrong} changes. Returns the way to stop. */
export function onDeviceClockChange(listener: () => void): () => void {
  deviceClockListeners.add(listener)
  return () => {
    deviceClockListeners.delete(listener)
  }
}

/** Whether this address signs in through the sign-in site rather than with B2C itself. */
export function isSignedInThroughHop(config: AuthConfig, origin: string = window.location.origin): boolean {
  return config.mode === 'b2c' && config.signInOrigin !== '' && config.signInOrigin !== origin
}

/** What a site starts a hop with; the API adds the site's own domain hint and policy. */
export interface HopStart {
  /** The typed address, `login_hint`; empty for a renewal. */
  readonly loginHint: string
  /** Whether B2C must ask for credentials, after a sign-in the site refused. */
  readonly isFresh: boolean
  /**
   * A one-time sign-in link's hint (SM-39): the hop then signs in with that link alone, through
   * the link's own B2C policy, which the API names.
   */
  readonly signInLink?: string
}

/** What B2C is asked for: the typed address, and the site's own domain hint or policy. */
export interface HopSignIn extends Omit<HopStart, 'signInLink'> {
  /** The site's domain hint, empty for none. */
  readonly domainHint: string
  /** The site's custom B2C policy, or a sign-in link's own; empty for none. */
  readonly policy: string
  /** A sign-in link's hint, B2C's `id_token_hint`; empty for none. */
  readonly idTokenHint: string
}

/** B2C's access token, as a hop brought it back. */
export interface HopToken {
  readonly accessToken: string
  /** Milliseconds since the epoch. */
  readonly expiresOn: number
}

const hopSchema = z.object({ hop: z.string().min(1) })
const hintsSchema = z.object({
  loginHint: z.string().nullish(),
  domainHint: z.string().nullish(),
  policy: z.string().nullish(),
  asksForCredentials: z.boolean(),
  signInLink: z.string().nullish(),
  returnTo: z.string().min(1),
})
const codeSchema = z.object({ code: z.string().min(1), returnTo: z.string().min(1) })
const tokenSchema = z.object({ accessToken: z.string().min(1), expiresOn: z.string().min(1) })
const storedTokenSchema = z.object({ accessToken: z.string().min(1), expiresOn: z.number() })
const pendingSchema = z.object({
  hop: z.string().min(1),
  isFreshBefore: z.boolean(),
  startedAt: z.number(),
  returnTo: z.string().min(1),
})
const signOutReturnSchema = z.object({ origin: z.string().min(1), startedAt: z.number() })
const identitySchema = z.object({ iss: z.string().min(1), sub: z.string().min(1) })
const renewalSchema = identitySchema.extend({ signOut: z.string().nullable() })
const startSchema = z.object({ verifier: z.string().min(43), renewal: renewalSchema.nullable() })
const sealedSchema = z.object({ iv: z.string().min(1), data: z.string().min(1), signOut: z.string().nullable() })

/** How long a sign-out may take at B2C before its way back is forgotten. */
const signOutLifetimeMs = 10 * 60_000

/** As long as the API keeps a hop: an older pending one is never finished. */
const hopLifetimeMs = 15 * 60_000

/**
 * On a site that signs in through the sign-in site: starts a hop and sends the browser to the
 * sign-in site with its id alone; the API keeps what to ask B2C for. Rejects, staying on the page,
 * when the API refuses.
 */
export async function startHop(
  apiBaseUrl: string,
  signInOrigin: string,
  start: HopStart,
  renewing: HopRenewal | null = null,
): Promise<void> {
  const verifier = randomKey()
  const challenge = await sha256(verifier)
  const answer = hopSchema.parse(
    await createApiClient(apiBaseUrl).post(hopsPath, {
      challenge,
      ...(start.loginHint !== '' && { loginHint: start.loginHint }),
      asksForCredentials: start.isFresh,
      ...(start.signInLink !== undefined && { signInLink: start.signInLink }),
    }),
  )

  // A renewal must bring back the same person, and nobody once any tab signed out after it began.
  write(sessionStorage, startKey, JSON.stringify({ verifier, renewal: renewing }))

  // The hop's id alone: where it goes back to is the API's record, never the address.
  const fragment = new URLSearchParams({ hop: answer.hop })
  hopNavigation.assign(`${signInOrigin}${hopPagePath}#${fragment.toString()}`)
}

/** Why a hop came back without a code, as the sign-in site says; the site shows it in its own frame. */
export type HopFailureKind = 'interrupted' | 'setup' | 'other' | 'forgotPassword'

const hopFailureKinds: readonly HopFailureKind[] = ['interrupted', 'setup', 'other', 'forgotPassword']

/** What a hop brought back to the callback: a code to exchange, or why there is none. */
export type HopResult = { readonly code: string } | { readonly failure: HopFailureKind }

/**
 * On a site that signs in through the sign-in site, before logging starts: takes what a hop brought
 * back to the callback, a code or a failure, out of the address bar and history, so no log ever
 * records it. `null` when the address holds neither.
 */
export function takeHopResult(redirectPath: string): HopResult | null {
  const { pathname, search, hash } = window.location
  // As the router shows the callback. MSAL matches no path: with navigateToLoginRequestUrl off it
  // takes its answer on whichever page it lands, so the router alone decides what the callback is.
  if (!isRoutePath(redirectPath, pathname)) {
    return null
  }
  const fragment = new URLSearchParams(hash.slice(1))
  const code = fragment.get('code')
  const failure = fragment.get('hop_error')
  if (code === null && failure === null) {
    return null
  }
  window.history.replaceState(window.history.state, '', `${pathname}${search}`)
  if (code !== null) {
    return { code }
  }
  // Only a kind this app knows; anything else reads as an interrupted sign-in.
  return { failure: hopFailureKinds.find((kind) => kind === failure) ?? 'interrupted' }
}

/**
 * On the sign-in site, before MSAL reads the address: takes a code its own hop brought back, as a
 * one-time sign-in link for this site makes one (SM-39), out of the address bar and history. Only
 * in a tab that started a hop, and never B2C's own answer, which always carries `state`. `null`
 * otherwise.
 */
export function takeOwnHopResult(redirectPath: string): HopResult | null {
  const fragment = new URLSearchParams(window.location.hash.slice(1))
  if (readRaw(sessionStorage, startKey) === null || fragment.has('state')) {
    return null
  }
  return takeHopResult(redirectPath)
}

/** The page a one-time sign-in link opens, on its own site (SM-39). */
export const signInLinkPath = '/auth/sign-in-link'

/** A sign-in link's hint, read from the address's fragment; `null` with none. */
export function readSignInLink(hash: string): string | null {
  const link = new URLSearchParams(hash.replace(/^#/, '')).get('link')
  return link === null || link === '' ? null : link
}

/** Forgets this tab's half of a hop that came back without a code. */
export function forgetHopStart(): void {
  remove(sessionStorage, startKey)
}

/**
 * On the sign-in site: goes back to a hop's site, leaving this site's history behind. A hop this
 * site started itself, for a sign-in link to it (SM-39), can come back to this very page with only
 * another fragment, which `replace` alone would not load, so that one is loaded afresh for its code
 * or failure to be read; any other address, this site's other pages included, is an ordinary move.
 */
export function returnFromHop(url: string): void {
  const target = new URL(url, window.location.href)
  const { origin, pathname, search } = window.location
  if (target.origin === origin && target.pathname === pathname && target.search === search) {
    hopNavigation.reload(url)
    return
  }
  hopNavigation.replace(url)
}

/** On the sign-in site: where a hop that failed goes back to, so its site shows why in its own frame. */
export function hopFailureAddress(returnTo: string, redirectPath: string, failure: HopFailureKind): string {
  return `${returnTo}${redirectPath}#${new URLSearchParams({ hop_error: failure }).toString()}`
}

/**
 * Before first render, on a site that signs in through the sign-in site: exchanges the code
 * {@link takeHopResult} took, keeping B2C's token. `none` when there was no code.
 */
export async function consumeHopCode(apiBaseUrl: string, code: string | null): Promise<'none' | 'signedIn'> {
  if (code === null) {
    return 'none'
  }

  const started = startSchema.safeParse(readJson(sessionStorage, startKey))
  remove(sessionStorage, startKey)
  if (!started.success) {
    throw new Error('The sign-in came back to a tab that did not start it. Sign in again.')
  }

  const { verifier, renewal: expected } = started.data
  const answer = tokenSchema.parse(await createApiClient(apiBaseUrl).post(`${hopsPath}/exchange`, { code, verifier }))

  const signedIn = identityOf(answer.accessToken)
  if (expected !== null && (signedIn === null || signedIn.iss !== expected.iss || signedIn.sub !== expected.sub)) {
    // Never another person's sign-in in place of this one's: both are forgotten here.
    await clearHopToken()
    throw new Error('The sign-in was renewed as a different person, so it was not used. Sign in again.')
  }
  // A renewal another tab's sign-out overtook brings nobody back: checked again as it is stored,
  // against the sign-out it began after, so one landing in between is never missed.
  const overtaken = new Error('You signed out while the sign-in was being renewed. Sign in again.')
  if (expected !== null && expected.signOut !== currentSignOut()) {
    throw overtaken
  }

  const returnedAt = Date.now()
  const token = { accessToken: answer.accessToken, expiresOn: Date.parse(answer.expiresOn) }
  if (!(await storeHopToken(token, expected === null ? currentSignOut() : expected.signOut)) && expected !== null) {
    throw overtaken
  }
  write(sessionStorage, hopReturnedKey, String(returnedAt))
  return 'signedIn'
}

/** Who a B2C token names, as B2C's `(iss, sub)`; `null` when it cannot be read. */
export interface HopIdentity {
  readonly iss: string
  readonly sub: string
}

/** A renewal: who must come back, and the last sign-out in any tab when it began. */
export interface HopRenewal extends HopIdentity {
  readonly signOut: string | null
}

/**
 * A renewal for the person this token names, begun now: take it before anything waits, so a
 * sign-out in another tab from then on overtakes it. `null` when the token names nobody.
 */
export function renewalFor(accessToken: string): HopRenewal | null {
  const identity = identityOf(accessToken)
  return identity === null ? null : { ...identity, signOut: currentSignOut() }
}

/** Who the token names; `null` when it cannot be read. Never verified here: the API does that. */
export function identityOf(accessToken: string): HopIdentity | null {
  const parsed = identitySchema.safeParse(claimsOf(accessToken))
  return parsed.success ? parsed.data : null
}

// The token as this tab last read it, so every read is synchronous.
let held: HopToken | null = null

// Moves on with every change this tab makes, so a load or store still decrypting or encrypting
// when the token changes never brings back what was there before.
let generation = 0

/** B2C's token a hop brought back, as last loaded or stored in this tab, or `null`. */
export function readHopToken(): HopToken | null {
  return held
}

/**
 * Loads the token every tab shares. It is kept encrypted under a key held in a cookie that ends
 * with the browser session, as MSAL keeps its own: once the browser closes, it can no longer be
 * read, and is dropped. A change this tab makes meanwhile wins; the answer is then that change.
 */
export async function loadHopToken(): Promise<HopToken | null> {
  const started = generation
  const raw = readRaw(localStorage, tokenKey)
  const stored = sealedSchema.safeParse(readJson(localStorage, tokenKey))
  const key = readSessionKey()
  // Stored before the last sign-out, as by a tab whose store raced it: signed out.
  if (!stored.success || key === null || stored.data.signOut !== currentSignOut()) {
    held = null
    if (stored.success) {
      await dropIfStill(raw)
    }
    return held
  }

  let loaded: HopToken | null
  try {
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64Url(stored.data.iv) },
      await importSessionKey(key),
      fromBase64Url(stored.data.data),
    )
    const parsed = storedTokenSchema.safeParse(JSON.parse(new TextDecoder().decode(plain)))
    loaded = parsed.success ? parsed.data : null
  } catch {
    // Sealed under another browser session's key.
    loaded = null
    if (started === generation) {
      await dropIfStill(raw)
    }
  }
  // Another tab signed out or in while this was decrypting: what is stored now is the answer.
  if (started === generation && readRaw(localStorage, tokenKey) !== raw) {
    return loadHopToken()
  }
  if (started === generation) {
    held = loaded
  }
  return held
}

// Removes the stored token if it is still `raw`, under the token's lock, so a store another tab
// makes meanwhile is never the one removed.
async function dropIfStill(raw: string | null): Promise<void> {
  await withTokenLock(() => {
    if (readRaw(localStorage, tokenKey) === raw) {
      remove(localStorage, tokenKey)
    }
  })
}

function readRaw(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Keeps the token for every tab of this browser session, encrypted (see {@link loadHopToken}),
 * unless any tab signed out after `signOut`, the last sign-out its sign-in began after: by default
 * the one now. Answers false when such a sign-out refused it.
 */
export async function storeHopToken(token: HopToken, signOut: string | null = currentSignOut()): Promise<boolean> {
  const stored = ++generation
  held = token
  const key = readSessionKey() ?? newSessionKey()
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const sealed = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    await importSessionKey(key),
    new TextEncoder().encode(JSON.stringify(token)),
  )
  // The check and the write together, so no tab's sign-out lands between them.
  let isRefused = false
  // Not narrowed to `null` here: the lock's callback sets it.
  let written = null as string | null
  await withTokenLock(() => {
    // Signed out in any tab since: nothing is kept, here or for the others.
    if (currentSignOut() !== signOut) {
      if (stored === generation) {
        held = null
      }
      isRefused = true
      return
    }
    // Another sign-in stored in this tab meanwhile: that one stands. It carries the last sign-out
    // it knew of, so it is never loaded after a later one.
    if (stored === generation) {
      written = JSON.stringify({ iv: base64Url(iv), data: base64Url(new Uint8Array(sealed)), signOut })
      write(localStorage, tokenKey, written)
    }
  })
  // Without Web Locks the check and the write are two steps: a sign-out another tab made between
  // them is caught here, and what this tab wrote is forgotten. One after this reaches it as an event.
  if (written !== null && currentSignOut() !== signOut) {
    if (stored === generation) {
      held = null
    }
    await dropIfStill(written)
    isRefused = true
  }
  return !isRefused
}

/**
 * Runs `work` while no other tab of this site checks for a sign-out and writes or forgets the
 * token; at once where the browser has no Web Locks.
 */
async function withTokenLock(work: () => void): Promise<void> {
  if (typeof navigator === 'undefined' || !('locks' in navigator)) {
    work()
    return
  }
  await navigator.locks.request(tokenLockName, work)
}

// The last sign-out in any tab, so a renewal or store begun before it keeps nothing.
function currentSignOut(): string | null {
  return readRaw(localStorage, signedOutKey)
}

/** Whether the token can still be sent, with a margin before it expires. */
export function isHopTokenUsable(token: HopToken, now: number = Date.now()): boolean {
  return token.expiresOn - expiryMarginMs > now
}

/**
 * Forgets the token, in every tab, at once. Resolves once it is also gone from a store another tab
 * had under way, so a caller about to leave the page awaits it; a token such a store still writes
 * carries the sign-out before this one, and no tab loads it.
 */
export function clearHopToken(): Promise<void> {
  generation++
  held = null
  write(localStorage, signedOutKey, `${String(Date.now())}-${randomKey()}`)
  remove(localStorage, tokenKey)
  // Locks are granted in turn, so a sign-in begun after this one stores after it.
  return withTokenLock(() => {
    remove(localStorage, tokenKey)
  })
}

/** On the sign-in site: what B2C answered for a pending hop, kept from the redirect for the callback. */
export interface HopSignInResult {
  readonly accessToken: string
}

let hopSignIn: HopSignInResult | null = null

/** On the sign-in site: keeps what B2C answered for the pending hop, in this tab's memory only. */
export function holdHopSignIn(result: HopSignInResult | null): void {
  hopSignIn = result
}

/** On the sign-in site: what B2C answered for the pending hop, or `null` when it signed nobody in. */
export function heldHopSignIn(): HopSignInResult | null {
  return hopSignIn
}

/** Whether a storage event is about the token. */
export function isHopTokenChange(event: StorageEvent): boolean {
  return event.storageArea === window.localStorage && (event.key === null || event.key === tokenKey)
}

/** A name to greet the person by from the token, display only; empty when it carries none. */
export function displayNameOf(accessToken: string): string {
  const parsed = z
    .object({ name: z.string().optional(), given_name: z.string().optional(), family_name: z.string().optional() })
    .safeParse(claimsOf(accessToken))
  if (!parsed.success) {
    return ''
  }
  const claims = parsed.data
  return (claims.name ?? [claims.given_name, claims.family_name].filter((part) => part !== undefined).join(' ')).trim()
}

// The token's payload, unverified, or null when it is not a JWT.
function claimsOf(accessToken: string): unknown {
  try {
    return JSON.parse(new TextDecoder().decode(fromBase64Url(accessToken.split('.')[1] ?? '')))
  } catch {
    return null
  }
}

/** On the sign-in site: the hop's id, read from the address's fragment; `null` with no hop. */
export function readHopId(hash: string): string | null {
  const hop = new URLSearchParams(hash.replace(/^#/, '')).get('hop')
  return hop === null || hop === '' ? null : hop
}

/** On the sign-in site: whether the API knows `origin` as an enabled site's base URL. */
export async function isKnownSite(apiBaseUrl: string, origin: string): Promise<boolean> {
  try {
    await createApiClient(apiBaseUrl, undefined, { siteBaseUrl: origin }).get('/site-management/v1/sites/current')
    return true
  } catch {
    return false
  }
}

/**
 * On the sign-in site: what the hop asks B2C for, as the API keeps it, never as the address says,
 * and the base URL it goes back to. The first read claims the hop for this browser, which keeps the
 * API's cookie for it: only this browser can finish it. Rejects when the API refuses, as for a hop
 * that expired or another browser claimed.
 */
export async function readHopSignIn(apiBaseUrl: string, hop: string): Promise<HopSignIn & { readonly returnTo: string }> {
  const hints = hintsSchema.parse(await createApiClient(apiBaseUrl).post(`${hopsPath}/hints`, { hop }))
  // The API's own record of where the hop started; checked anyway, so nothing else is ever a destination.
  if (new URL(hints.returnTo).origin !== hints.returnTo) {
    throw new Error('The sign-in site answered an address that is not a site origin.')
  }
  return {
    loginHint: hints.loginHint ?? '',
    domainHint: hints.domainHint ?? '',
    policy: hints.policy ?? '',
    isFresh: hints.asksForCredentials,
    idTokenHint: hints.signInLink ?? '',
    returnTo: hints.returnTo,
  }
}

/** On the sign-in site: a hop waiting for B2C's answer. */
export interface PendingHop {
  readonly hop: string
  /** Whether this site already asked B2C for credentials before the hop, as it goes on doing once the hop is done. */
  readonly isFreshBefore: boolean
  /** When it started, in milliseconds since the epoch. */
  readonly startedAt: number
  /** The base URL the hop started from, as the API keeps it: where the person goes back to, whatever happens. */
  readonly returnTo: string
}

/** Remembers the hop B2C's answer finishes, in this tab; from now on it says where to go back to. */
export function rememberPendingHop(pending: Omit<PendingHop, 'startedAt'>): void {
  write(sessionStorage, pendingKey, JSON.stringify({ ...pending, startedAt: Date.now() }))
}

/** The hop B2C's answer finishes, however old, or `null`; left in place. */
export function readPendingHop(): PendingHop | null {
  const parsed = pendingSchema.safeParse(readJson(sessionStorage, pendingKey))
  return parsed.success ? parsed.data : null
}

/** Whether a pending hop is older than the API keeps a hop, so it can no longer be finished. */
export function isPendingHopExpired(pending: PendingHop, now: number = Date.now()): boolean {
  return now - pending.startedAt >= hopLifetimeMs
}

/**
 * Where a hop that failed goes back to: the site the API's record of the pending hop names, while
 * the API still keeps that hop; `null` with none, and then the person stays on this site's sign-in page.
 */
export function readPendingHopReturn(now: number = Date.now()): string | null {
  const pending = readPendingHop()
  return pending === null || isPendingHopExpired(pending, now) ? null : pending.returnTo
}

/** Forgets the pending hop. */
export function clearPendingHop(): void {
  remove(sessionStorage, pendingKey)
}

/**
 * On the sign-in site, when B2C signed nobody in for a pending hop, as when the person canceled:
 * forgets it, and puts back whether this site asks B2C for credentials. An account the hop signed
 * in is the caller's to forget (the callback's `AbandonHop`).
 */
export function abandonPendingHop(): void {
  const pending = readPendingHop()
  clearPendingHop()
  if (pending !== null && !pending.isFreshBefore) {
    clearFreshSignIn()
  }
}

/**
 * On the sign-in site, once B2C signed the person in: finishes the hop with B2C's token. Answers
 * where to send the browser: the hop's site's callback, the code in the fragment.
 */
export async function finishHop(apiBaseUrl: string, accessToken: string, hop: string, redirectPath: string): Promise<string> {
  const client = createApiClient(apiBaseUrl, () => Promise.resolve(accessToken))
  const answer = codeSchema.parse(await client.post(`${hopsPath}/codes`, { hop }))
  // The API's own record of where the hop started; checked anyway, so nothing else is ever a destination.
  if (new URL(answer.returnTo).origin !== answer.returnTo) {
    throw new Error('The sign-in site answered an address that is not a site origin.')
  }
  return `${answer.returnTo}${redirectPath}#${new URLSearchParams({ code: answer.code }).toString()}`
}

/**
 * On a site that signs in through the sign-in site: sends the browser to the sign-in site's
 * sign-out, which ends B2C's session and comes back to this site's sign-in page.
 */
export function signOutThroughSignInSite(signInOrigin: string): void {
  hopNavigation.assign(`${signInOrigin}${hopSignOutPath}#${new URLSearchParams({ return: window.location.origin }).toString()}`)
}

/** On the sign-in site: the site a sign-out came from, read from the fragment; `null` with none. */
export function readSignOutReturn(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, '')).get('return')
  if (value === null) {
    return null
  }
  try {
    return new URL(value).origin === value ? value : null
  } catch {
    return null
  }
}

/** On the sign-in site: remembers, across B2C's sign-out, the site to go back to. */
export function rememberSignOutReturn(origin: string): void {
  write(sessionStorage, signOutReturnKey, JSON.stringify({ origin, startedAt: Date.now() }))
}

/**
 * On the sign-in site: the site a finished sign-out goes back to, or `null`, as for one that
 * started too long ago to be this callback's; left in place.
 */
export function readStoredSignOutReturn(now: number = Date.now()): string | null {
  const parsed = signOutReturnSchema.safeParse(readJson(sessionStorage, signOutReturnKey))
  if (!parsed.success || now - parsed.data.startedAt >= signOutLifetimeMs) {
    return null
  }
  return readSignOutReturn(new URLSearchParams({ return: parsed.data.origin }).toString())
}

/** On the sign-in site: forgets the site a sign-out goes back to. */
export function clearSignOutReturn(): void {
  remove(sessionStorage, signOutReturnKey)
}

/** B2C's sign-out address for the configured policy, coming back to `postLogoutRedirectUri`. */
export function b2cSignOutUrl(authority: string, postLogoutRedirectUri: string): string {
  return `${authority}/oauth2/v2.0/logout?${new URLSearchParams({ post_logout_redirect_uri: postLogoutRedirectUri }).toString()}`
}

function randomKey(): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(32)))
}

async function sha256(value: string): Promise<string> {
  return base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))))
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/[=]+$/, '')
}

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/')), (character) => character.charCodeAt(0))
}

// The key, or null with none or more than one of that name: a second one was set by someone else.
function readSessionKey(): string | null {
  const prefix = `${sessionKeyCookie()}=`
  const cookies = document.cookie.split(';').map((part) => part.trim()).filter((part) => part.startsWith(prefix))
  return cookies.length !== 1 || cookies[0]!.length === prefix.length ? null : cookies[0]!.slice(prefix.length)
}

// A session cookie on this host only, so the key, and with it the token, ends with the browser session.
function newSessionKey(): string {
  const key = randomKey()
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${sessionKeyCookie()}=${key}; Path=/; SameSite=Strict${secure}`
  return key
}

function importSessionKey(key: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', fromBase64Url(key), 'AES-GCM', false, ['encrypt', 'decrypt'])
}

function readJson(storage: Storage, key: string): unknown {
  try {
    const value = storage.getItem(key)
    return value === null ? null : JSON.parse(value)
  } catch {
    return null
  }
}

function write(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value)
  } catch {
    // Storage blocked: the hop cannot be finished in this tab, and sign-in says so.
  }
}

function remove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key)
  } catch {
    // Nothing kept.
  }
}
